import React, { useState, useEffect } from "react";
import "./Circular_Form.css";
import cancel from "../../../assets/cancel.png";
import Loader from "../../Loader/Loader";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import api from "../../../utils/api";

// ZOD schema
const schema = z.object({
  title: z.string().min(1, "Title is required"),
  created: z.string().min(1, "Date is required"),
  picture: z
    .any()
    .refine((file) => file instanceof File || typeof file === "string", {
      message: "Attachment is required",
    }),
  notice: z.string().min(1, "Notice is required"),
  sections: z.array(z.string()).min(1, "Select at least one section"),
});

const Circular_Form = (props) => {
  const isEdit = props.isEdit;
  const data = props.data;

  const [loading, setLoading] = useState(false);
  const [selectedSections, setSelectedSections] = useState(
    isEdit ? JSON.parse(data?.considering)?.considering || [] : []
  );
  const [image, setImage] = useState(null);

  // react-hook-form setup
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues: {
      title: isEdit ? data?.title : "",
      created: isEdit
        ? new Date(data?.created).toISOString().split("T")[0]
        : "",
      notice: isEdit ? data?.notice : "",
      picture: "",
      sections: isEdit ? JSON.parse(data?.considering)?.considering || [] : [],
    },
  });

  // Ensure "sections" is registered
  useEffect(() => {
    register("sections");
  }, [register]);

  const handleClickClose = () => {
    props.setDisp();
    props.setEdit();
  };

  const handleCheckboxChange = (e) => {
    const value = e.target.value;
    let newSections = [...selectedSections];
    if (e.target.checked) {
      newSections.push(value);
    } else {
      newSections = newSections.filter((v) => v !== value);
    }
    setSelectedSections(newSections);
    setValue("sections", newSections, { shouldValidate: true });
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    setImage(file);
    setValue("picture", file, { shouldValidate: true });
  };

  const onSubmit = async (formData) => {
    try {
      setLoading(true);

      // Local Upload
      let attachment = { url: "", name: "" };

      if (!isEdit || image) {
        // Upload file to backend local storage
        const uploadForm = new FormData();
        uploadForm.append("file", image);
        const uploadRes = await api.post("/circulars/upload", uploadForm, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        attachment = { url: uploadRes.data.url, name: uploadRes.data.name };
      } else {
        attachment = JSON.parse(data.attachment);
      }

      const circularData = {
        ...formData,
        attachment: attachment,
        considering: { considering: formData.sections },
      };

      if (!isEdit) {
        await api.post(`/circulars`, circularData);
        toast.success("Circular added successfully!");
      } else {
        await api.put(`/circulars/${data.id}`, circularData);
        toast.success("Circular updated successfully!");
      }

      setLoading(false);
      if (props.onSuccess) props.onSuccess();
      props.setDisp();
      props.setEdit();
    } catch (err) {
      console.error("Submission error:", err);
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="circular__form_div_open">
        <div className="circular__form">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
          <h1 style={{margin: '0', fontSize: '24px'}}>{isEdit ? "Edit Circular" : "Add Circular"}</h1>
          <div className="circular__from_img" onClick={handleClickClose}>
            <img src={cancel} alt="cancel" />
          </div>
          </div>

          <div className="circular__form_input">
            <h5>New Circular</h5>
            <input
              type="text"
              placeholder="Enter circular title"
              {...register("title")}
              className="circular__form_input_small"
            />
            {errors.title && <p className="error">{errors.title.message}</p>}
          </div>

          <div className="circular__form_input">
            <h5>Circular Date</h5>
            <input
              type="date"
              placeholder="Select date"
              {...register("created")}
              className="date"
              min={new Date().toISOString().split("T")[0]}
              style={{width: '100%'}}
            />
            {errors.created && (
              <p className="error">{errors.created.message}</p>
            )}
          </div>

          <div className="circular__form_input">
            <h5>Attachment</h5>
            <input
              type="file"
              accept=".xlsx,.xls,.doc,.docx,.ppt,.csv,.pptx,.txt,.pdf,.png,.jpg,.jpeg"
              onChange={handleUpload}
              required={!isEdit}
            />
            <div className="filename">
              <label>
                {image?.name || (isEdit && JSON.parse(data?.attachment)?.name)}
              </label>
              {(image?.name || (isEdit && data?.attachment)) && (
                <span style={{ marginLeft: 10 }}>
                  <a
                    href={image ? URL.createObjectURL(image) : JSON.parse(data.attachment).url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "green",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    View
                  </a>
                </span>
              )}
            </div>
            {errors.picture && (
              <p className="error">{errors.picture.message}</p>
            )}
          </div>

          <div className="circular__form_input big">
            <h5>Circular Notice</h5>
            <textarea
              placeholder="Enter circular notice"
              {...register("notice")}
              className="circular__form_input_small"
            />
            {errors.notice && <p className="error">{errors.notice.message}</p>}
          </div>

          <div className="circular__form_input_checkbox" style={{padding: '20px 0 20px 0'}}>
            {[
              { label: "Pre Nursery", value: "PRE-NURSERY" },
              { label: "Nursery", value: "NURSERY" },
              { label: "Pre Primary", value: "PRE-PRIMARY" },
              { label: "Teacher", value: "TEACHER" },
              { label: "Primary", value: "PRIMARY" },
              { label: "Secondary", value: "SECONDARY" },
            ].map((item, index) => (
              <div className="form-group" style={index % 2 === 0 ? {padding: '0'} : {padding: '0', justifyContent: 'flex-start'}} key={item.value}>
                <input
                  type="checkbox"
                  value={item.value}
                  checked={selectedSections.includes(item.value)}
                  onChange={handleCheckboxChange}
                />
                <label>{item.label}</label>
              </div>
            ))}
            {errors.sections && (
              <p className="error">{errors.sections.message}</p>
            )}
          </div>

          <div>
            {loading ? (
              <Loader small={true} />
            ) : (
              <button className="circular__form_button" type="submit">
                <span style={{ color: "#fff" }}>
                  {isEdit ? "Update Circular" : "Add Circular"}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default Circular_Form;